import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Heading,
  Spinner,
  Stack
} from '@chakra-ui/react'
import { Helmet } from 'react-helmet'
import { capabilities } from '@homevest/utils'
import startCase from 'lodash/startCase'
import { useSelector } from 'react-redux'

import { hasCapability } from 'lib/admin-perms'
import axios from 'lib/axios'

import StepCard from './components/StepCard'
import VerifyDataFreshness from './steps/VerifyDataFreshness'
import ReviewRecipients from './steps/ReviewRecipients'
import ReviewAdjustments from './steps/ReviewAdjustments'
import SnapshotRebuild from './steps/SnapshotRebuild'
import SyncStatements from './steps/SyncStatements'
import UploadRecipientsBQ from './steps/UploadRecipientsBQ'
import TestSend from './steps/TestSend'
import LiveSend from './steps/LiveSend'
import UpdateConfigurations from './steps/UpdateConfigurations'
import {
  ShipStatusResponse,
  getDefaultStatementEndDate,
  getErrorMessage,
  isInCurrentMonth,
  isSameMonthOrAfter
} from './types'

const { DAGGER } = capabilities.CAPABILITY_TYPES

const ShipStatements = () => {
  const isDagger = useSelector((store: any) => hasCapability(store.admin, DAGGER))

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [shipStatus, setShipStatus] = useState<ShipStatusResponse | null>(null)
  const [statementEndDate, setStatementEndDate] = useState(getDefaultStatementEndDate())
  const [completedSteps, setCompletedSteps] = useState<Record<number, boolean>>({
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
    6: false,
    7: false,
    8: false,
    9: false
  })

  const isFresh = useMemo(() => {
    if (!shipStatus?.maxStatementDate || !statementEndDate) {
      return false
    }
    return isSameMonthOrAfter(shipStatus.maxStatementDate, statementEndDate)
  }, [shipStatus, statementEndDate])

  const alreadyShippedThisMonth = useMemo(
    () => isInCurrentMonth(shipStatus?.lastShipDate),
    [shipStatus?.lastShipDate]
  )
  const hasStatementEndDate = Boolean(statementEndDate)

  const isStepLocked = (step: number): boolean => {
    if (step <= 1) {
      return false
    }
    if (!hasStatementEndDate) {
      return true
    }
    if (alreadyShippedThisMonth) {
      return true
    }
    return !completedSteps[step - 1]
  }

  const setStepChecked = (step: number, checked: boolean) => {
    setCompletedSteps((previous) => ({
      ...previous,
      [step]: checked
    }))
  }

  const loadShipStatus = async () => {
    try {
      setIsLoading(true)
      setError('')
      const { data } = await axios.get('/admin/statements/ship-status')
      setShipStatus(data)
    } catch (err: any) {
      setError(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadShipStatus()
  }, [])

  if (!isDagger) {
    return (
      <Alert status='error'>
        <AlertIcon />
        <AlertTitle>Access denied</AlertTitle>
        <AlertDescription>You must have DAGGER capability to use this page.</AlertDescription>
      </Alert>
    )
  }

  return (
    <>
      <Helmet>
        <title>Statements | Ship Statements</title>
      </Helmet>
      <Box p={4} w='100%' maxW='1200px' mx='auto'>
        <Heading mb={4}>Ship Statements</Heading>
        <Stack spacing={4}>
          {alreadyShippedThisMonth && (
            <Alert status='warning'>
              <AlertIcon />
              <Stack>
                <AlertTitle>Statements already shipped this month</AlertTitle>
                <AlertDescription>
                  {startCase('last_ship_date')} indicates a send occurred this month. Steps 2-9 are
                  locked.
                </AlertDescription>
              </Stack>
            </Alert>
          )}
          {error && (
            <Alert status='error'>
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {isLoading && <Spinner />}

          <StepCard
            step={1}
            title='Verify Data Freshness'
            status={isFresh ? 'success' : 'warning'}
            infoTooltip='Purpose: confirm upstream data is recent enough for this shipment month. This checks MAX(date) in BigQuery table dbt_prod.monthly_customer_statements and passes when that date is in the same month as, or later than, the selected Statement End Date.'
            isCompletionDisabled={!hasStatementEndDate}
            isChecked={completedSteps[1]}
            onCheckedChange={(checked) => setStepChecked(1, checked)}
          >
            <VerifyDataFreshness
              shipStatus={shipStatus}
              statementEndDate={statementEndDate}
              isLoading={isLoading}
              onRefresh={loadShipStatus}
              onStatementEndDateChange={setStatementEndDate}
            />
          </StepCard>

          <StepCard
            step={2}
            title='Review & Update Statement Recipients'
            infoTooltip='Purpose: preview and apply the monthly recipient set. This updates rentals.is_statements_enabled in Postgres so downstream steps operate on the reviewed audience.'
            isLocked={isStepLocked(2)}
            isChecked={completedSteps[2]}
            onCheckedChange={(checked) => setStepChecked(2, checked)}
          >
            <ReviewRecipients statementEndDate={statementEndDate} />
          </StepCard>

          <StepCard
            step={3}
            title='Review Wallet Adjustments'
            infoTooltip='Purpose: QA large month-over-month wallet adjustments before sending. Large adjustments can indicate late ledger entries or historical methodology changes that should be explained before customers receive statements.'
            isLocked={isStepLocked(3)}
            isChecked={completedSteps[3]}
            completionLabel='Mark step as complete (I have reviewed these adjustments).'
            onCheckedChange={(checked) => setStepChecked(3, checked)}
          >
            <ReviewAdjustments statementEndDate={statementEndDate} />
          </StepCard>

          <StepCard
            step={4}
            title='Snapshot & Rebuild'
            infoTooltip='Purpose: rebuild property_financials_final from staging and generate monthly snapshots. These snapshots are the reference points used to calculate wallet adjustments in customer statements.'
            isLocked={isStepLocked(4)}
            isChecked={completedSteps[4]}
            onCheckedChange={(checked) => setStepChecked(4, checked)}
          >
            <SnapshotRebuild statementEndDate={statementEndDate} />
          </StepCard>

          <StepCard
            step={5}
            title='Sync statements_raw'
            infoTooltip='Purpose: sync refreshed statement rows into Postgres statements_raw, which is consumed by statement generation and app/email statement views.'
            isLocked={isStepLocked(5)}
            isChecked={completedSteps[5]}
            onCheckedChange={(checked) => setStepChecked(5, checked)}
          >
            <SyncStatements statementEndDate={statementEndDate} />
          </StepCard>

          <StepCard
            step={6}
            title='Upload Statement Recipients to BigQuery'
            infoTooltip='Purpose: persist the exact reviewed audience for this run into BigQuery dataset statement_recipients as statements_YYYY_MM so send counts and historical recipients are auditable.'
            isLocked={isStepLocked(6)}
            isChecked={completedSteps[6]}
            onCheckedChange={(checked) => setStepChecked(6, checked)}
          >
            <UploadRecipientsBQ statementEndDate={statementEndDate} />
          </StepCard>

          <StepCard
            step={7}
            title='Test Send Statements'
            infoTooltip='Purpose: run a dry test publish to calculator-send-monthly-statements and verify expected counts/log output before any live customer emails or Buildium impact.'
            isLocked={isStepLocked(7)}
            isChecked={completedSteps[7]}
            onCheckedChange={(checked) => setStepChecked(7, checked)}
          >
            <TestSend statementEndDate={statementEndDate} />
          </StepCard>

          <StepCard
            step={8}
            title='Send Statements Live'
            infoTooltip='Purpose: perform the live publish that sends customer statements and triggers Buildium journal entry side effects. This should be executed once after all checks pass.'
            isLocked={isStepLocked(8)}
            isChecked={completedSteps[8]}
            onCheckedChange={(checked) => setStepChecked(8, checked)}
          >
            <LiveSend statementEndDate={statementEndDate} />
          </StepCard>

          <StepCard
            step={9}
            title='Update Statement Configurations'
            infoTooltip='Purpose: write statement_configurations.app_end_date and statement_configurations.last_ship_date in Postgres so the system knows which statement month was shipped and which snapshot window to use next cycle.'
            isLocked={isStepLocked(9)}
            isChecked={completedSteps[9]}
            onCheckedChange={(checked) => setStepChecked(9, checked)}
          >
            <UpdateConfigurations shipStatus={shipStatus} />
          </StepCard>
        </Stack>
      </Box>
    </>
  )
}

export default ShipStatements
